import { useParams } from 'react-router-dom'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import styled, { keyframes } from 'styled-components'
import { Card, Form } from 'react-bootstrap'
import toast from 'react-hot-toast'
import RouteContainer from '../../common/styles/RouteContainer'
import RowSpacer from '../../common/styles/RowSpacer'
import NoContentForFullScreen from '../../common/NoContentForFullScreen'
import useAdminBookAuthor from './useAdminBookAuthor'
import type AdminBookAuthorResponse from './AdminBookAuthorResponse'
import useHtmlTitle from '../../common/useHtmlTitle'
import booksitoutIcon from '../../config/BooksitoutIcon'
import CardBodyContentContainer from '../../common/styles/CardBodyContentContainer'
import LoadingBar from '../../common/LoadingBar'
import { getBookLanguageDisplayName } from '../book/BookType'
import type { BookLanguage } from '../book/BookType'
import BookIsbnCard, { BookIsbnCardGrid } from '../book/common/BookIsbnCard'
import BookEditCoverModal from '../book/edit/BookEditCoverModal'
import type BookIsbnResponse from '../book/isbn/BookIsbnResponse'
import BooksitoutImages from '../../images/BooksitoutImages'
import useGridColumns from '../../common/hooks/useGridColumns'
import Modal from '../../common/Modal'
import BooksitoutButton from '../../common/button/BooksitoutButton'
import { BooksitoutServer } from '../../config/BooksitoutServer'

const NATIONALITY_LABEL: Record<string, string> = {
	US: '🇺🇸 미국',
	CHINA: '🇨🇳 중국',
	GERMANY: '🇩🇪 독일',
	JAPAN: '🇯🇵 일본',
	INDIA: '🇮🇳 인도',
	UK: '🇬🇧 영국',
	FRANCE: '🇫🇷 프랑스',
	ITALY: '🇮🇹 이탈리아',
	BRAZIL: '🇧🇷 브라질',
	CANADA: '🇨🇦 캐나다',
	RUSSIA: '🇷🇺 러시아',
	SOUTH_KOREA: '🇰🇷 대한민국',
	SPAIN: '🇪🇸 스페인',
	AUSTRALIA: '🇦🇺 호주',
	MEXICO: '🇲🇽 멕시코',
	INDONESIA: '🇮🇩 인도네시아',
	NETHERLANDS: '🇳🇱 네덜란드',
	SAUDI_ARABIA: '🇸🇦 사우디아라비아',
	TURKEY: '🇹🇷 튀르키예',
	TAIWAN: '🇹🇼 대만',
	ISRAEL: '🇮🇱 이스라엘',
	SWEDEN: '🇸🇪 스웨덴',
	SOUTH_AFRICA: '🇿🇦 남아프리카',
	PORTUGAL: '🇵🇹 포르투갈',
	COLOMBIA: '🇨🇴 콜롬비아',
	AUSTRIA: '🇦🇹 오스트리아',
	IRELAND: '🇮🇪 아일랜드',
	BELGIUM: '🇧🇪 벨기에',
	GREECE: '🇬🇷 그리스',
	IRAN: '🇮🇷 이란',
	NORWAY: '🇳🇴 노르웨이',
	NEW_ZEALAND: '🇳🇿 뉴질랜드',
	POLAND: '🇵🇱 폴란드',
	CZECH: '🇨🇿 체코',
	HUNGARY: '🇭🇺 헝가리',
	SWITZERLAND: '🇨🇭 스위스',
	CHILE: '🇨🇱 칠레',
	ARGENTINA: '🇦🇷 아르헨티나',
	PERU: '🇵🇪 페루',
	DENMARK: '🇩🇰 덴마크',
	FINLAND: '🇫🇮 핀란드',
	EGYPT: '🇪🇬 이집트',
	NIGERIA: '🇳🇬 나이지리아',
	UKRAINE: '🇺🇦 우크라이나',
	VIETNAM: '🇻🇳 베트남',
	PHILIPPINES: '🇵🇭 필리핀',
	SINGAPORE: '🇸🇬 싱가포르',
	ICELAND: '🇮🇸 아이슬란드',
	ROMANIA: '🇷🇴 루마니아',
}

const AdminAuthorEditModal: React.FC<{
	isOpen: boolean
	onClose: () => void
	author: AdminBookAuthorResponse['author']
	urlName: string
	onSaved: () => void
}> = ({ isOpen, onClose, author, urlName, onSaved }) => {
	const [name, setName] = useState(author.name)
	const [nationality, setNationality] = useState(author.nationality ?? '')
	const [description, setDescription] = useState(author.description ?? '')
	const [imageUrl, setImageUrl] = useState(author.imageUrl ?? '')
	const [imageFile, setImageFile] = useState<File | null>(null)
	const [imagePreview, setImagePreview] = useState<string | null>(author.imageUrl ?? null)

	const NAME_FIELDS = [
		{ key: 'nameEnglish', label: '영어 (English)' },
		{ key: 'nameChinese', label: '중국어 (中文)' },
		{ key: 'nameJapanese', label: '일본어 (日本語)' },
		{ key: 'nameSpanish', label: '스페인어 (Español)' },
		{ key: 'nameFrench', label: '프랑스어 (Français)' },
		{ key: 'nameGerman', label: '독일어 (Deutsch)' },
		{ key: 'nameRussian', label: '러시아어 (Русский)' },
		{ key: 'nameItalian', label: '이탈리아어 (Italiano)' },
		{ key: 'namePortuguese', label: '포르투갈어 (Português)' },
		{ key: 'nameArabic', label: '아랍어 (العربية)' },
		{ key: 'nameHindi', label: '힌디어 (हिन्दी)' },
		{ key: 'nameIndonesian', label: '인도네시아어 (Indonesia)' },
		{ key: 'nameDutch', label: '네덜란드어 (Nederlands)' },
		{ key: 'nameTurkish', label: '튀르키예어 (Türkçe)' },
		{ key: 'nameSwedish', label: '스웨덴어 (Svenska)' },
		{ key: 'nameHebrew', label: '히브리어 (עברית)' },
	] as const

	const [names, setNames] = useState<Record<string, string>>(() =>
		Object.fromEntries(NAME_FIELDS.map(f => [f.key, author[f.key as keyof typeof author] ?? '']))
	)

	useEffect(() => {
		setName(author.name)
		setNationality(author.nationality ?? '')
		setDescription(author.description ?? '')
		setImageUrl(author.imageUrl ?? '')
		setImagePreview(author.imageUrl ?? null)
		setImageFile(null)
		setNames(Object.fromEntries(NAME_FIELDS.map(f => [f.key, author[f.key as keyof typeof author] ?? ''])))
	}, [author])

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) return
		setImageFile(file)
		setImageUrl('')
		const reader = new FileReader()
		reader.onload = () => setImagePreview(reader.result as string)
		reader.readAsDataURL(file)
	}

	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()

		const formData = new FormData()
		if (name) formData.append('name', name)
		if (nationality) formData.append('nationality', nationality)
		if (description) formData.append('description', description)
		for (const field of NAME_FIELDS) {
			if (names[field.key]) formData.append(field.key, names[field.key])
		}
		if (imageFile) {
			formData.append('image', imageFile)
		} else if (imageUrl) {
			formData.append('imageUrl', imageUrl)
		}

		BooksitoutServer
			.put(`/v1/admin/book-author/${urlName}`, formData)
			.then(() => {
				toast.success('작가 정보를 수정했어요.')
				onSaved()
				onClose()
			})
			.catch(() => toast.error('오류가 났어요. 잠시 후 다시 시도해 주세요.'))
	}

	return (
		<Modal
			titleText={'작가 수정하기'}
			isShowing={isOpen}
			onClose={onClose}
			size={'lg'}
			body={
				<Form onSubmit={onSubmit}>
					<Form.Group>
						<Form.Label>이름</Form.Label>
						<Form.Control type="text" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
					</Form.Group>
					<RowSpacer size={10} />

					<NameSectionLabel>다국어 이름</NameSectionLabel>
					<NameGrid>
						{NAME_FIELDS.map(field => (
							<Form.Group key={field.key}>
								<Form.Label style={{ fontSize: 13 }}>{field.label}</Form.Label>
								<Form.Control
									type="text"
									value={names[field.key]}
									onChange={(e) => setNames(prev => ({ ...prev, [field.key]: e.target.value }))}
									autoComplete="off"
									size="sm"
								/>
							</Form.Group>
						))}
					</NameGrid>
					<RowSpacer size={10} />

					<Form.Group>
						<Form.Label>국적</Form.Label>
						<Form.Select value={nationality} onChange={(e) => setNationality(e.target.value)}>
							<option value="">선택 안함</option>
							{Object.entries(NATIONALITY_LABEL).map(([key, label]) => (
								<option key={key} value={key}>{label}</option>
							))}
						</Form.Select>
					</Form.Group>
					<RowSpacer size={10} />

					<Form.Group>
						<Form.Label>설명</Form.Label>
						<Form.Control as="textarea" rows={10} style={{ height: 'auto' }} value={description} onChange={(e) => setDescription(e.target.value)} />
						<RowSpacer size={5} />
						<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
						<AppendButton
							type="button"
							disabled={description.includes('(출처: Wikipedia)')}
							onClick={() => setDescription(prev => prev.trimEnd() + '\n\n(출처: Wikipedia)')}
						>
							+ 출처: Wikipedia
						</AppendButton>
						</div>
					</Form.Group>
					<RowSpacer size={10} />

					<ImageRow>
						<ImageCol>
							<Form.Group>
								<Form.Label>이미지 업로드</Form.Label>
								<input type="file" accept="image/*" onChange={handleFileChange} id="author-image-upload" style={{ display: 'none' }} />
								<div>
									<FileUploadButton type="button" onClick={() => document.getElementById('author-image-upload')?.click()}>
										{imageFile ? imageFile.name : '파일 선택'}
									</FileUploadButton>
								</div>
							</Form.Group>
						</ImageCol>
						<ImageCol>
							<Form.Group>
								<Form.Label>이미지 URL</Form.Label>
								<Form.Control type="text" value={imageUrl} onChange={(e) => { setImageUrl(e.target.value); setImageFile(null); setImagePreview(e.target.value || null) }} autoComplete="off" />
							</Form.Group>
						</ImageCol>
					</ImageRow>
					{imagePreview && (
						<>
							<RowSpacer size={10} />
							<div className="d-flex justify-content-center">
								<PreviewImage src={imagePreview} alt="미리보기" onError={(e) => (e.currentTarget.style.display = 'none')} />
							</div>
						</>
					)}
					<RowSpacer size={30} />

					<BooksitoutButton message={'수정하기'} />
				</Form>
			}
		/>
	)
}

const OriginalIsbnModal: React.FC<{
	isOpen: boolean
	onClose: () => void
	book: BookIsbnResponse
	allBooks: BookIsbnResponse[]
	onSaved: () => void
}> = ({ isOpen, onClose, book, allBooks, onSaved }) => {
	const [manualIsbn, setManualIsbn] = useState(book.originalIsbn13 ?? '')
	const [searchQuery, setSearchQuery] = useState('')
	const [searchResults, setSearchResults] = useState<{ isbn13: string; title: string; authors: string; coverUrl: string | null }[]>([])
	const [isSearching, setIsSearching] = useState(false)

	const otherBooks = allBooks.filter(b => b.isbn13 !== book.isbn13 && b.language !== book.language)

	const handleLink = (originalIsbn: string) => {
		const formData = new FormData()
		formData.append('originalIsbn', originalIsbn)

		BooksitoutServer
			.put(`/v1/admin/book-isbn/${book.isbn13}`, formData)
			.then(() => {
				toast.success(`원서 연결 완료: ${originalIsbn}`)
				onSaved()
				onClose()
			})
			.catch(() => toast.error('오류가 났어요. 잠시 후 다시 시도해 주세요.'))
	}

	const handleSearch = () => {
		if (!searchQuery.trim()) return
		setIsSearching(true)
		BooksitoutServer
			.get(`/v1/book/search/by-query?q=${encodeURIComponent(searchQuery)}&size=12`)
			.then(res => setSearchResults(res.data))
			.catch(() => toast.error('검색 오류'))
			.finally(() => setIsSearching(false))
	}

	return (
		<Modal
			titleText={`원서 연결 - ${book.title}`}
			isShowing={isOpen}
			onClose={onClose}
			size={'lg'}
			body={
				<>
					{book.originalIsbn13 && (
						<>
							<OriginalIsbnCurrentLabel>현재 연결: <strong>{book.originalIsbn13}</strong></OriginalIsbnCurrentLabel>
							<RowSpacer size={10} />
						</>
					)}

					<Form.Group>
						<Form.Label>ISBN 직접 입력</Form.Label>
						<OriginalIsbnInputRow>
							<Form.Control
								type="text"
								placeholder="ISBN13"
								value={manualIsbn}
								onChange={e => setManualIsbn(e.target.value)}
								autoComplete="off"
							/>
							<LinkButton type="button" disabled={!manualIsbn.trim()} onClick={() => handleLink(manualIsbn.trim())}>
								연결
							</LinkButton>
						</OriginalIsbnInputRow>
					</Form.Group>

					{otherBooks.length > 0 && (
						<>
							<RowSpacer />
							<Form.Label>같은 작가의 다른 언어 도서</Form.Label>
							<OriginalIsbnBookList>
								{otherBooks.map(b => (
									<OriginalIsbnBookItem key={b.isbn13} onClick={() => handleLink(b.isbn13)} $isLinked={b.isbn13 === book.originalIsbn13}>
										<OriginalIsbnBookCover
											src={b.coverUrl || BooksitoutImages.Placeholder.bookCover}
											alt={b.title}
											onError={(e: any) => e.target.src = BooksitoutImages.Placeholder.bookCover}
										/>
										<OriginalIsbnBookInfo>
											<OriginalIsbnBookTitle className="clamp-1-line">{b.title}</OriginalIsbnBookTitle>
											<OriginalIsbnBookMeta>{b.isbn13} · {getBookLanguageDisplayName(b.language)}</OriginalIsbnBookMeta>
										</OriginalIsbnBookInfo>
									</OriginalIsbnBookItem>
								))}
							</OriginalIsbnBookList>
						</>
					)}

					<RowSpacer />
					<Form.Label>검색</Form.Label>
					<OriginalIsbnInputRow>
						<Form.Control
							type="text"
							placeholder="제목 또는 저자"
							value={searchQuery}
							onChange={e => setSearchQuery(e.target.value)}
							onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleSearch())}
							autoComplete="off"
						/>
						<LinkButton type="button" onClick={handleSearch} disabled={isSearching}>
							검색
						</LinkButton>
					</OriginalIsbnInputRow>

					{searchResults.length > 0 && (
						<>
							<RowSpacer size={10} />
							<OriginalIsbnBookList>
								{searchResults.map(b => (
									<OriginalIsbnBookItem key={b.isbn13} onClick={() => handleLink(b.isbn13)}>
										<OriginalIsbnBookCover
											src={b.coverUrl || BooksitoutImages.Placeholder.bookCover}
											alt={b.title}
											onError={(e: any) => e.target.src = BooksitoutImages.Placeholder.bookCover}
										/>
										<OriginalIsbnBookInfo>
											<OriginalIsbnBookTitle className="clamp-1-line">{b.title}</OriginalIsbnBookTitle>
											<OriginalIsbnBookMeta>{b.isbn13}</OriginalIsbnBookMeta>
										</OriginalIsbnBookInfo>
									</OriginalIsbnBookItem>
								))}
							</OriginalIsbnBookList>
						</>
					)}
				</>
			}
		/>
	)
}

const AdminAuthorRoute = () => {
	const { urlName } = useParams()
	const [data, isLoading, isError, refetch] = useAdminBookAuthor(urlName ?? null)
	const [gridRef, columns] = useGridColumns()
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	const [coverEditBook, setCoverEditBook] = useState<{ isbn13: string; title: string; authors: string; coverUrl: string | null } | null>(null)
	const [coverEditCover, setCoverEditCover] = useState<string | null>(null)
	const [linkBook, setLinkBook] = useState<BookIsbnResponse | null>(null)

	const [selectedIsbns, setSelectedIsbns] = useState<string[]>([])
	const [representativeIsbn, setRepresentativeIsbn] = useState<string | null>(null)
	const [mappingMemos, setMappingMemos] = useState<Record<string, string>>({})
	const [memoEditIsbn, setMemoEditIsbn] = useState<string | null>(null)

	const isMappingMode = selectedIsbns.length > 0

	const toggleSelectIsbn = (isbn: string) => {
		setSelectedIsbns(prev => {
			if (prev.includes(isbn)) {
				const next = prev.filter(i => i !== isbn)
				if (representativeIsbn === isbn) {
					setRepresentativeIsbn(next.length > 0 ? next[0] : null)
				}
				setMappingMemos(m => { const copy = { ...m }; delete copy[isbn]; return copy })
				return next
			} else {
				const next = [...prev, isbn]
				if (next.length === 1) setRepresentativeIsbn(isbn)
				return next
			}
		})
	}

	const handleSaveMapping = () => {
		if (selectedIsbns.length < 2 || !representativeIsbn) {
			toast.error('2개 이상의 책을 선택해 주세요.')
			return
		}

		BooksitoutServer
			.post('/v1/admin/book-isbn-mapping', {
				representativeIsbn,
				mappings: selectedIsbns
					.filter(i => i !== representativeIsbn)
					.map(isbn => ({ isbn, memo: mappingMemos[isbn] || null })),
			})
			.then(() => {
				toast.success('같은 책 매핑을 저장했어요.')
				setSelectedIsbns([])
				setRepresentativeIsbn(null)
				setMappingMemos({})
				setMemoEditIsbn(null)
			})
			.catch(() => toast.error('오류가 났어요. 잠시 후 다시 시도해 주세요.'))
	}

	const handleCancelMapping = () => {
		setSelectedIsbns([])
		setRepresentativeIsbn(null)
		setMappingMemos({})
		setMemoEditIsbn(null)
	}

	const cardRefs = useRef<Record<string, HTMLDivElement | null>>({})
	const gridContainerRef = useRef<HTMLDivElement | null>(null)
	const [outlineRects, setOutlineRects] = useState<{ color: string; top: number; left: number; width: number; height: number }[]>([])

	const MAPPING_COLORS = ['#007bff', '#6f42c1', '#e83e8c', '#fd7e14', '#20c997', '#17a2b8']

	const mappings = data?.mappings ?? []
	const books = data?.books ?? []

	const isbnToMappingGroup: Record<string, { groupIndex: number; representativeIsbn: string; memo: string | null }> = {}
	mappings.forEach((group, i) => {
		isbnToMappingGroup[group.representativeIsbn] = { groupIndex: i, representativeIsbn: group.representativeIsbn, memo: null }
		group.members.forEach(m => {
			isbnToMappingGroup[m.isbn] = { groupIndex: i, representativeIsbn: group.representativeIsbn, memo: m.memo }
		})
	})

	const reorderBooks = useCallback((bookList: typeof books) => {
		const reordered: typeof books = []
		const usedGroups = new Set<number>()
		for (const book of bookList) {
			const mapping = isbnToMappingGroup[book.isbn13]
			if (mapping && !usedGroups.has(mapping.groupIndex)) {
				usedGroups.add(mapping.groupIndex)
				reordered.push(...bookList.filter(b => isbnToMappingGroup[b.isbn13]?.groupIndex === mapping.groupIndex))
			} else if (!mapping) {
				reordered.push(book)
			}
		}
		return reordered
	}, [isbnToMappingGroup])

	const computeOutlines = useCallback((containerEl: HTMLElement | null) => {
		if (!containerEl) return
		const containerRect = containerEl.getBoundingClientRect()
		const groups = new Map<number, string[]>()
		for (const [isbn, mapping] of Object.entries(isbnToMappingGroup)) {
			if (!groups.has(mapping.groupIndex)) groups.set(mapping.groupIndex, [])
			groups.get(mapping.groupIndex)!.push(isbn)
		}

		const rects: typeof outlineRects = []
		groups.forEach((isbns, groupIndex) => {
			let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
			let found = false
			for (const isbn of isbns) {
				const el = cardRefs.current[isbn]
				if (!el) continue
				found = true
				const r = el.getBoundingClientRect()
				minX = Math.min(minX, r.left - containerRect.left)
				minY = Math.min(minY, r.top - containerRect.top)
				maxX = Math.max(maxX, r.right - containerRect.left)
				maxY = Math.max(maxY, r.bottom - containerRect.top)
			}
			if (found) {
				const pad = 6
				rects.push({
					color: MAPPING_COLORS[groupIndex % MAPPING_COLORS.length],
					top: minY - pad,
					left: minX - pad,
					width: maxX - minX + pad * 2,
					height: maxY - minY + pad * 2,
				})
			}
		})
		setOutlineRects(rects)
	}, [isbnToMappingGroup])

	useEffect(() => {
		const timer = setTimeout(() => computeOutlines(gridContainerRef.current), 50)
		const onResize = () => computeOutlines(gridContainerRef.current)
		window.addEventListener('resize', onResize)
		return () => { clearTimeout(timer); window.removeEventListener('resize', onResize) }
	}, [computeOutlines, books, searchQuery])

	useHtmlTitle(data?.author.name ?? '작가 관리')

	if (isLoading) return (
		<RouteContainer>
			<RowSpacer size={40} />
			<SkeletonHeader>
				<SkeletonCircle />
				<RowSpacer size={15} />
				<SkeletonBar width={200} height={32} />
				<RowSpacer size={10} />
				<SkeletonBar width={150} height={18} />
				<RowSpacer size={8} />
				<SkeletonBar width={100} height={16} />
			</SkeletonHeader>
			<RowSpacer size={30} />
			<LoadingBar size={3} />
			<RowSpacer size={30} />
			<BookIsbnCardGrid ref={gridRef}>
				{Array.from({ length: columns || 7 }).map((_, i) => (
					<div key={i}>
						<Card className="h-100">
							<CardBodyContentContainer height={0} backgroundColorType='card'>
								<SkeletonBookCover />
								<RowSpacer size={10} />
								<LoadingBar size={3} />
								<RowSpacer size={5} />
								<LoadingBar size={2} />
							</CardBodyContentContainer>
						</Card>
					</div>
				))}
			</BookIsbnCardGrid>
			<RowSpacer size={40} />
		</RouteContainer>
	)
	if (isError || data == null) return <NoContentForFullScreen message={'작가 정보를 찾을 수 없어요'} />

	const { author } = data

	const handleSaved = () => {
		refetch(true)
	}

	const renderBookCard = (book: typeof books[0]) => {
		return (
		<AdminBookCardWrapper key={book.isbn13} ref={(el: HTMLDivElement | null) => { cardRefs.current[book.isbn13] = el }}>
			<BookIsbnCard book={book} />
			<SelectCheckButton
				$selected={selectedIsbns.includes(book.isbn13)}
				onClick={(e) => { e.stopPropagation(); toggleSelectIsbn(book.isbn13) }}
				title="같은 책 묶기"
			>
				<booksitoutIcon.check size={16} />
			</SelectCheckButton>
			{isMappingMode && (
				<MappingSelectOverlay
					$selected={selectedIsbns.includes(book.isbn13)}
					onClick={() => toggleSelectIsbn(book.isbn13)}
				/>
			)}
			{selectedIsbns.includes(book.isbn13) && (
				<>
					<StarBadge
						$isRepresentative={representativeIsbn === book.isbn13}
						onClick={(e) => { e.stopPropagation(); setRepresentativeIsbn(book.isbn13) }}
					>
						{representativeIsbn === book.isbn13
							? <booksitoutIcon.starFill size={16} />
							: <booksitoutIcon.star size={16} />}
					</StarBadge>
					{representativeIsbn !== book.isbn13 && (
						<MemoButton
							$hasMemo={!!mappingMemos[book.isbn13]}
							onClick={(e) => { e.stopPropagation(); setMemoEditIsbn(memoEditIsbn === book.isbn13 ? null : book.isbn13) }}
							title="메모"
						>
							<booksitoutIcon.edit size={14} />
						</MemoButton>
					)}
					{memoEditIsbn === book.isbn13 && (
						<MemoInputWrapper onClick={(e) => e.stopPropagation()}>
							<Form.Control
								type="text"
								placeholder="메모 (예: e-book)"
								value={mappingMemos[book.isbn13] ?? ''}
								onChange={e => setMappingMemos(prev => ({ ...prev, [book.isbn13]: e.target.value }))}
								autoComplete="off"
								size="sm"
								maxLength={20}
								autoFocus
								onKeyDown={e => e.key === 'Enter' && setMemoEditIsbn(null)}
							/>
						</MemoInputWrapper>
					)}
				</>
			)}
			{!isMappingMode && (
				<>
					<CoverEditOverlay onClick={() => { setCoverEditBook(book); setCoverEditCover(book.coverUrl) }} title="표지 수정" />
					<LinkIconButton $linked={!!book.originalIsbn13} onClick={(e) => { e.stopPropagation(); setLinkBook(book) }} title="원서 연결">
						<booksitoutIcon.convert size={14} />
					</LinkIconButton>
				</>
			)}
		</AdminBookCardWrapper>
	)}

	const renderBookGrid = (bookList: typeof books) => {
		const reordered = reorderBooks(bookList)
		return (
			<MappingGridWrapper ref={gridContainerRef}>
				{outlineRects.map((rect, i) => (
					<MappingOutlineOverlay
						key={`outline-${i}`}
						style={{
							top: rect.top,
							left: rect.left,
							width: rect.width,
							height: rect.height,
							borderColor: rect.color,
						}}
					/>
				))}
				<BookIsbnCardGrid ref={gridRef}>
					{reordered.map(renderBookCard)}
				</BookIsbnCardGrid>
			</MappingGridWrapper>
		)
	}

	const handleCoverSelect = (coverUrl: string | null) => {
		if (!coverEditBook || !coverUrl) return

		const formData = new FormData()
		formData.append('coverUrl', coverUrl)

		BooksitoutServer
			.put(`/v1/admin/book-isbn/${coverEditBook.isbn13}`, formData)
			.then(() => {
				toast.success('표지를 수정했어요.')
				refetch(true)
			})
			.catch(() => toast.error('오류가 났어요. 잠시 후 다시 시도해 주세요.'))
	}

	return (
		<>
			{linkBook && (
				<OriginalIsbnModal
					isOpen={!!linkBook}
					onClose={() => setLinkBook(null)}
					book={linkBook}
					allBooks={books}
					onSaved={() => refetch(true)}
				/>
			)}

			{coverEditBook && (
				<BookEditCoverModal
					isOpen={!!coverEditBook}
					onClose={() => setCoverEditBook(null)}
					cover={coverEditCover}
					setCover={handleCoverSelect}
					currentCover={coverEditBook.coverUrl}
					title={coverEditBook.title}
					author={coverEditBook.authors}
				/>
			)}

			{isEditModalOpen && (
				<AdminAuthorEditModal
					isOpen={isEditModalOpen}
					onClose={() => setIsEditModalOpen(false)}
					author={author}
					urlName={urlName!}
					onSaved={handleSaved}
				/>
			)}

			<RouteContainer>
				<AdminBanner>ADMIN</AdminBanner>
				<RowSpacer size={40} />

				<ClickableAuthorHeader onClick={() => setIsEditModalOpen(true)}>
				{author.imageUrl ? (
					<AuthorImage src={author.imageUrl} alt={author.name} />
				) : (
					<AuthorIconWrapper>
						<booksitoutIcon.user size={40} />
					</AuthorIconWrapper>
				)}
				<RowSpacer size={15} />
				<AuthorName>{author.name}</AuthorName>
				{author.originalName && <AuthorOriginalName>{author.originalName}</AuthorOriginalName>}
				{author.nationality && <AuthorNationality>{NATIONALITY_LABEL[author.nationality] ?? author.nationality}</AuthorNationality>}
				{author.description && (
					<>
						<RowSpacer size={10} />
						<AuthorDescription>{author.description}</AuthorDescription>
					</>
				)}
			</ClickableAuthorHeader>

			<RowSpacer size={30} />

			{isMappingMode && (
				<MappingFloatingBar>
					<MappingInfo>{selectedIsbns.length}개 선택됨</MappingInfo>
					<MappingActionButton onClick={handleSaveMapping} disabled={selectedIsbns.length < 2}>
						저장
					</MappingActionButton>
					<MappingCancelButton onClick={handleCancelMapping}>
						취소
					</MappingCancelButton>
				</MappingFloatingBar>
			)}

			<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
				<Form.Control
					type="text"
					placeholder="제목으로 검색"
					value={searchQuery}
					onChange={e => setSearchQuery(e.target.value)}
					autoComplete="off"
					style={{ maxWidth: 300, fontSize: 14 }}
				/>
			</div>
			<RowSpacer size={10} />

			{(() => {
				const filteredBooks = searchQuery.trim()
					? books.filter(b => b.title.toLowerCase().includes(searchQuery.trim().toLowerCase()))
					: books
				const grouped = Object.entries(
					filteredBooks.reduce((groups, book) => {
						const lang = book.language ?? 'UNKNOWN'
						if (!groups[lang]) groups[lang] = []
						groups[lang].push(book)
						return groups
					}, {} as Record<string, typeof books>)
				)
				const multiLang = grouped.length >= 2

				return multiLang ? (
					grouped.map(([language, groupBooks]) => (
						<div key={language}>
							<SectionHeader>
								<SectionTitle>도서 ({getBookLanguageDisplayName(language as BookLanguage)})</SectionTitle>
								<SectionCount>{groupBooks.length}</SectionCount>
							</SectionHeader>
							<RowSpacer size={10} />
							{renderBookGrid(groupBooks)}
							<RowSpacer />
						</div>
					))
				) : (
					<>
						<SectionHeader>
							<SectionTitle>도서</SectionTitle>
							<SectionCount>{filteredBooks.length}</SectionCount>
						</SectionHeader>
						<RowSpacer size={10} />
						{renderBookGrid(filteredBooks)}
					</>
				)
			})()}

			<RowSpacer size={40} />
		</RouteContainer>
		</>
	)
}

const skeletonGlow = keyframes`
	0% { background-color: #e0e0e0; }
	40% { background-color: #d6d6d6; }
	60% { background-color: #c8c8c8; }
	100% { background-color: #e0e0e0; }
`

const SkeletonHeader = styled.div`
	text-align: center;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding-bottom: 20px;
`

const SkeletonBar = styled.div<{ width: number; height: number }>`
	width: ${props => props.width}px;
	height: ${props => props.height}px;
	background-color: #e0e0e0;
	border-radius: 4px;
	animation: ${skeletonGlow} 2s infinite;
`

const SkeletonCircle = styled.div`
	width: 100px;
	height: 100px;
	border-radius: 50%;
	background-color: #e0e0e0;
	margin: 0 auto;
	animation: ${skeletonGlow} 2s infinite;
`

const SkeletonBookCover = styled.div`
	height: 180px;
	background-color: #e0e0e0;
	border-radius: 6px;
	animation: ${skeletonGlow} 2s infinite;
`

const AuthorHeader = styled.div`
	text-align: center;
`

const ClickableAuthorHeader = styled(AuthorHeader)`
	cursor: pointer;
	border-radius: 16px;
	padding: 20px;

	&:hover {
		background-color: var(--hover-bg-color);
	}
`

const AuthorImage = styled.img`
	width: 100px;
	height: 100px;
	border-radius: 50%;
	object-fit: cover;
`

const AuthorIconWrapper = styled.div`
	width: 100px;
	height: 100px;
	border-radius: 50%;
	background-color: var(--card-card-bg-color);
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto;
	color: var(--sub-text-color);
`

const AuthorName = styled.h1`
	font-size: 2rem;
	font-weight: bold;
	color: var(--text-color);
`

const AuthorOriginalName = styled.h2`
	font-size: 1.2rem;
	color: var(--sub-text-color);
	font-style: italic;
`

const AuthorNationality = styled.p`
	font-size: 14px;
	color: var(--sub-text-color);
	margin: 0;
`

const AuthorDescription = styled.p`
	font-size: 15px;
	color: var(--text-color);
	line-height: 1.7;
	max-width: 700px;
	margin: 0 auto;
`

const SectionHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding-top: 20px;
	padding-bottom: 10px;
`

const SectionTitle = styled.h3`
	font-size: 1.2rem;
	font-weight: bold;
	color: var(--text-color);
	margin: 0;
`

const SectionCount = styled.span`
	font-size: 1rem;
	color: var(--sub-text-color);
`

const NameSectionLabel = styled.div`
	font-weight: 600;
	font-size: 14px;
	color: var(--text-color);
	margin-bottom: 8px;
`

const NameGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
`

const AppendButton = styled.button`
	padding: 4px 10px;
	border: 1px solid var(--sub-text-color);
	border-radius: 6px;
	background-color: var(--card-card-bg-color);
	color: var(--text-color);
	font-size: 13px;
	cursor: pointer;

	&:hover:not(:disabled) {
		background-color: var(--hover-bg-color);
	}

	&:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}
`

const ImageRow = styled.div`
	display: flex;
	gap: 12px;
`

const ImageCol = styled.div`
	flex: 1;
`

const FileUploadButton = styled.button`
	width: 100%;
	padding: 8px 16px;
	border: 1px solid var(--sub-text-color);
	border-radius: 8px;
	background-color: var(--card-card-bg-color);
	color: var(--text-color);
	font-size: 14px;
	cursor: pointer;

	&:hover {
		background-color: var(--hover-bg-color);
	}
`

const PreviewImage = styled.img`
	width: 100px;
	height: 100px;
	border-radius: 50%;
	object-fit: cover;
`


const AdminBookCardWrapper = styled.div`
	position: relative;
`

const CoverEditOverlay = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 190px;
	cursor: pointer;
	z-index: 1;
`

const LinkIconButton = styled.button<{ $linked?: boolean }>`
	position: absolute;
	right: 6px;
	bottom: 6px;
	width: 28px;
	height: 28px;
	border-radius: 50%;
	border: none;
	background-color: ${props => props.$linked ? '#1CB15AFF' : '#6c757d'};
	color: white;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	z-index: 1;

	&:hover {
		background-color: ${props => props.$linked ? '#18a050' : '#5a6268'};
	}
`

const OriginalIsbnCurrentLabel = styled.div`
	font-size: 14px;
	color: var(--text-color);
	padding: 8px 12px;
	background-color: var(--card-card-bg-color);
	border-radius: 8px;
`

const OriginalIsbnInputRow = styled.div`
	display: flex;
	gap: 8px;
`

const LinkButton = styled.button`
	padding: 6px 16px;
	border: none;
	border-radius: 8px;
	background-color: #6c757d;
	color: white;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	white-space: nowrap;

	&:hover:not(:disabled) {
		background-color: #5a6268;
	}

	&:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}
`

const OriginalIsbnBookList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	max-height: 300px;
	overflow-y: auto;
`

const OriginalIsbnBookItem = styled.div<{ $isLinked?: boolean }>`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px;
	border-radius: 8px;
	cursor: pointer;
	${props => props.$isLinked && `
		background-color: var(--hover-bg-color);
		outline: 2px solid #28a745;
	`}

	&:hover {
		background-color: var(--hover-bg-color);
	}
`

const OriginalIsbnBookCover = styled.img`
	width: 36px;
	height: 50px;
	object-fit: cover;
	border-radius: 4px;
`

const OriginalIsbnBookInfo = styled.div`
	flex: 1;
	min-width: 0;
`

const OriginalIsbnBookTitle = styled.div`
	font-size: 14px;
	color: var(--text-color);
`

const OriginalIsbnBookMeta = styled.div`
	font-size: 12px;
	color: var(--sub-text-color);
`

const MappingFloatingBar = styled.div`
	position: fixed;
	bottom: 30px;
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 12px 24px;
	background-color: var(--card-card-bg-color);
	border: 1px solid var(--sub-text-color);
	border-radius: 16px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
	z-index: 100;
`

const MappingInfo = styled.span`
	font-size: 14px;
	color: var(--text-color);
	font-weight: 600;
`

const MappingActionButton = styled.button`
	padding: 6px 16px;
	border: none;
	border-radius: 8px;
	background-color: #28a745;
	color: white;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;

	&:hover:not(:disabled) {
		background-color: #218838;
	}

	&:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}
`

const MappingCancelButton = styled.button`
	padding: 6px 16px;
	border: 1px solid var(--sub-text-color);
	border-radius: 8px;
	background-color: var(--card-card-bg-color);
	color: var(--text-color);
	font-size: 14px;
	cursor: pointer;

	&:hover {
		background-color: var(--hover-bg-color);
	}
`

const MappingSelectOverlay = styled.div<{ $selected?: boolean }>`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	cursor: pointer;
	z-index: 2;
	border-radius: 8px;
	border: ${props => props.$selected ? '3px solid #28a745' : '3px solid transparent'};
	background-color: ${props => props.$selected ? 'rgba(40, 167, 69, 0.08)' : 'transparent'};

	&:hover {
		background-color: rgba(40, 167, 69, 0.05);
	}
`

const MappingGridWrapper = styled.div`
	position: relative;
`

const MappingOutlineOverlay = styled.div`
	position: absolute;
	border: 2px solid;
	border-radius: 12px;
	pointer-events: none;
	z-index: 0;
`

const MemoButton = styled.button<{ $hasMemo?: boolean }>`
	position: absolute;
	top: 6px;
	right: 36px;
	z-index: 3;
	width: 26px;
	height: 26px;
	border-radius: 50%;
	border: none;
	background-color: ${props => props.$hasMemo ? '#17a2b8' : 'rgba(128, 128, 128, 0.5)'};
	color: white;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;

	&:hover {
		background-color: ${props => props.$hasMemo ? '#138496' : 'rgba(128, 128, 128, 0.8)'};
	}
`

const MemoInputWrapper = styled.div`
	position: absolute;
	top: 36px;
	right: 6px;
	z-index: 4;
	width: 160px;
`

const SelectCheckButton = styled.button<{ $selected?: boolean }>`
	position: absolute;
	top: 6px;
	right: 6px;
	z-index: 3;
	width: 26px;
	height: 26px;
	border-radius: 50%;
	border: none;
	background-color: ${props => props.$selected ? '#28a745' : 'rgba(128, 128, 128, 0.5)'};
	color: white;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	opacity: ${props => props.$selected ? 1 : 0.6};

	&:hover {
		opacity: 1;
		background-color: ${props => props.$selected ? '#218838' : 'rgba(128, 128, 128, 0.8)'};
	}
`

const StarBadge = styled.button<{ $isRepresentative?: boolean }>`
	position: absolute;
	top: 6px;
	left: 6px;
	z-index: 3;
	width: 28px;
	height: 28px;
	border-radius: 50%;
	border: none;
	background-color: ${props => props.$isRepresentative ? '#ffc107' : 'rgba(128, 128, 128, 0.6)'};
	color: ${props => props.$isRepresentative ? '#fff' : '#fff'};
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;

	&:hover {
		background-color: ${props => props.$isRepresentative ? '#e0a800' : 'rgba(128, 128, 128, 0.8)'};
	}
`

const AdminBanner = styled.div`
	margin-top: 20px;
	background-color: #dc3545;
	color: white;
	text-align: center;
	padding: 8px;
	font-weight: bold;
	font-size: 14px;
	border-radius: 8px;
	letter-spacing: 2px;
`

export default AdminAuthorRoute
